import { Platform } from 'react-native';
import Purchases, {
  CustomerInfo,
  LOG_LEVEL,
  PURCHASES_ARE_COMPLETED_BY_TYPE,
  PurchasesPackage,
  PurchasesStoreProduct,
  STOREKIT_VERSION,
} from 'react-native-purchases';
import {
  REVENUECAT_CONFIG,
  SUBSCRIPTION_PRODUCT_IDS,
  isRevenueCatConfigured,
} from '../config/revenuecat';
import { SubscriptionTier } from '../types/subscription';

export interface RevenueCatProduct {
  id: string;
  tier: SubscriptionTier;
  platform: 'ios' | 'android';
  price: number;
  currency: string;
  billingPeriod: 'monthly' | 'yearly';
  trialDays?: number;
  description: string;
  title?: string;
  localizedPrice?: string;
  packageIdentifier?: string;
  revenueCatPackage?: PurchasesPackage;
}

class RevenueCatService {
  private initialized = false;
  private currentUserId: string | null = null;

  isAvailable(): boolean {
    return Platform.OS !== 'web' && isRevenueCatConfigured();
  }

  private getApiKey(): string | null {
    if (Platform.OS === 'ios') return REVENUECAT_CONFIG.apiKeyIOS || null;
    if (Platform.OS === 'android') return REVENUECAT_CONFIG.apiKeyAndroid || null;
    return null;
  }

  async initialize(userId?: string): Promise<void> {
    if (!this.isAvailable()) return;

    const apiKey = this.getApiKey();
    if (!apiKey) {
      console.warn('RevenueCat: API key missing for this platform');
      return;
    }

    if (__DEV__) {
      Purchases.setLogLevel(LOG_LEVEL.DEBUG);
    }

    if (!this.initialized) {
      const config: Parameters<typeof Purchases.configure>[0] = {
        apiKey,
        appUserID: userId,
      };

      // SubscriptionStoreView completes purchases in StoreKit, not via Purchases.purchase*.
      if (Platform.OS === 'ios') {
        config.purchasesAreCompletedBy = {
          type: PURCHASES_ARE_COMPLETED_BY_TYPE.MY_APP,
          storeKitVersion: STOREKIT_VERSION.STOREKIT_2,
        };
      }

      await Purchases.configure(config);
      this.initialized = true;
      this.currentUserId = userId ?? null;
      return;
    }

    if (userId && userId !== this.currentUserId) {
      const { customerInfo } = await Purchases.logIn(userId);
      this.currentUserId = userId;
      console.log('RevenueCat: logged in user', userId, customerInfo.originalAppUserId);
    }
  }

  async logOut(): Promise<void> {
    if (!this.initialized) return;
    try {
      await Purchases.logOut();
      this.currentUserId = null;
    } catch (error) {
      console.warn('RevenueCat: logOut failed', error);
    }
  }

  async getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!this.isAvailable() || !this.initialized) return null;
    try {
      return await Purchases.getCustomerInfo();
    } catch (error) {
      console.error('RevenueCat: getCustomerInfo failed', error);
      return null;
    }
  }

  getTierFromCustomerInfo(customerInfo: CustomerInfo | null): SubscriptionTier {
    if (!customerInfo) return 'free';

    const { entitlements } = REVENUECAT_CONFIG;
    const activeSubs = customerInfo.activeSubscriptions;
    const hasPremiumSub = activeSubs.some((id) => id.includes('premium'));
    const hasStandardSub = activeSubs.some((id) => id.includes('standard'));
    const hasPremiumEnt = Boolean(customerInfo.entitlements.active[entitlements.premium]);
    const hasStandardEnt = Boolean(customerInfo.entitlements.active[entitlements.standard]);

    // activeSubscriptions can update before entitlements after a StoreKit plan change.
    if (hasPremiumSub || hasPremiumEnt) return 'premium';
    if (hasStandardSub || hasStandardEnt) return 'standard';
    return 'free';
  }

  getActiveProductId(customerInfo: CustomerInfo | null): string | undefined {
    if (!customerInfo) return undefined;

    const activeSubs = customerInfo.activeSubscriptions;
    const premiumFirst = ['premium_yearly', 'premium_monthly', 'standard_yearly', 'standard_monthly'] as const;
    for (const id of premiumFirst) {
      if (activeSubs.includes(id)) return id;
    }

    const active =
      customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlements.premium] ??
      customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlements.standard];

    return active?.productIdentifier;
  }

  isEntitlementActive(customerInfo: CustomerInfo | null): boolean {
    return this.getTierFromCustomerInfo(customerInfo) !== 'free';
  }

  private mapStoreProduct(product: PurchasesStoreProduct, pkg?: PurchasesPackage): RevenueCatProduct {
    const productId = product.identifier;
    return {
      id: productId,
      tier: productId.includes('premium') ? 'premium' : 'standard',
      platform: Platform.OS === 'ios' ? 'ios' : 'android',
      price: product.price,
      currency: product.currencyCode,
      billingPeriod: productId.includes('yearly') ? 'yearly' : 'monthly',
      description: product.description,
      title: product.title,
      localizedPrice: product.priceString,
      packageIdentifier: pkg?.identifier,
      revenueCatPackage: pkg,
    };
  }

  async getAvailableProducts(): Promise<RevenueCatProduct[]> {
    if (!this.isAvailable() || !this.initialized) return [];

    try {
      const offerings = await Purchases.getOfferings();
      const current = offerings.current ?? offerings.all[REVENUECAT_CONFIG.offeringId];

      if (current?.availablePackages?.length) {
        return current.availablePackages
          .filter((pkg) =>
            SUBSCRIPTION_PRODUCT_IDS.includes(
              pkg.product.identifier as (typeof SUBSCRIPTION_PRODUCT_IDS)[number]
            )
          )
          .map((pkg) => this.mapStoreProduct(pkg.product, pkg));
      }

      const products = await Purchases.getProducts([...SUBSCRIPTION_PRODUCT_IDS]);
      return products.map((p) => this.mapStoreProduct(p));
    } catch (error) {
      console.error('RevenueCat: getAvailableProducts failed', error);
      return [];
    }
  }

  async purchaseProduct(productId: string): Promise<{
    success: boolean;
    customerInfo?: CustomerInfo;
    error?: string;
    userCancelled?: boolean;
  }> {
    if (!this.isAvailable() || !this.initialized) {
      return { success: false, error: 'RevenueCat is not configured' };
    }

    try {
      const offerings = await Purchases.getOfferings();
      const current = offerings.current ?? offerings.all[REVENUECAT_CONFIG.offeringId];
      const pkg = current?.availablePackages.find(
        (p) => p.product.identifier === productId
      );

      let customerInfo: CustomerInfo;
      if (pkg) {
        const result = await Purchases.purchasePackage(pkg);
        customerInfo = result.customerInfo;
      } else {
        const products = await Purchases.getProducts([productId]);
        const product = products[0];
        if (!product) {
          return { success: false, error: 'Product not found in store' };
        }
        const result = await Purchases.purchaseStoreProduct(product);
        customerInfo = result.customerInfo;
      }

      return { success: true, customerInfo };
    } catch (error: unknown) {
      const purchasesError = error as { userCancelled?: boolean; message?: string };
      if (purchasesError?.userCancelled) {
        return { success: false, userCancelled: true, error: 'Purchase cancelled' };
      }
      return {
        success: false,
        error: purchasesError?.message ?? 'Purchase failed',
      };
    }
  }

  async restorePurchases(): Promise<{
    success: boolean;
    customerInfo?: CustomerInfo;
    error?: string;
  }> {
    if (!this.isAvailable() || !this.initialized) {
      return { success: false, error: 'RevenueCat is not configured' };
    }

    try {
      const customerInfo = await Purchases.restorePurchases();
      const hasActive = this.isEntitlementActive(customerInfo);
      return {
        success: hasActive,
        customerInfo,
        error: hasActive ? undefined : 'No active subscription found',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Restore failed',
      };
    }
  }

  getManagementURL(customerInfo: CustomerInfo | null): string | undefined {
    return customerInfo?.managementURL ?? undefined;
  }

  /** Push StoreKit transactions (e.g. SubscriptionStoreView) to RevenueCat. */
  async syncStorePurchases(): Promise<void> {
    if (!this.isAvailable() || !this.initialized) return;
    try {
      await Purchases.syncPurchases();
    } catch (error) {
      console.warn('RevenueCat: syncPurchases failed', error);
    }
  }

  /** Force Apple receipt refresh + RevenueCat customer info update. */
  async refreshCustomerInfoFromStore(): Promise<CustomerInfo | null> {
    if (!this.isAvailable() || !this.initialized) return null;
    try {
      await Purchases.syncPurchases();
    } catch (error) {
      console.warn('RevenueCat: syncPurchases failed', error);
    }
    try {
      const restored = await Purchases.restorePurchases();
      if (__DEV__) {
        console.log('RevenueCat refresh', {
          activeSubscriptions: restored.activeSubscriptions,
          premium: restored.entitlements.active[REVENUECAT_CONFIG.entitlements.premium]?.isActive,
          standard: restored.entitlements.active[REVENUECAT_CONFIG.entitlements.standard]?.isActive,
        });
      }
      return restored;
    } catch (error) {
      console.warn('RevenueCat: restorePurchases failed', error);
      return this.getCustomerInfo();
    }
  }

  onCustomerInfoUpdated(listener: (info: CustomerInfo) => void): () => void {
    if (!this.isAvailable()) {
      return () => undefined;
    }
    Purchases.addCustomerInfoUpdateListener(listener);
    return () => {
      Purchases.removeCustomerInfoUpdateListener(listener);
    };
  }
}

export const revenueCatService = new RevenueCatService();
